import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dayDatesInclusive } from '@baerenfang/shared';
import { CalendarGrid } from '../components/CalendarGrid';
import { addMonthsToKey, clampMonth } from '../lib/months';
import { todayInZurich } from '../lib/today';
import { claimantLabels, DEFAULT_CLAIM_BEDS, editedBeds, parseBedsInput } from '../lib/poll-board';
import {
  addPollComment,
  ApiClientError,
  claimPollSlot,
  enterPoll,
  fetchPollBoard,
  fetchPollThread,
  setPollName,
  updatePollClaimBeds,
  withdrawPollClaim,
  type PollBoard,
  type PollBoardSlot,
  type PollThreadComment,
} from '../lib/api';

const BTN_PRIMARY =
  'rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-1.5 text-sm font-medium disabled:opacity-50';
const BTN_SECONDARY =
  'rounded-md border border-slate-300 bg-white text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 px-3 py-1.5 text-sm font-medium disabled:opacity-50';
const INPUT =
  'rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm';

type Stage = 'loading' | 'error' | 'intro' | 'name' | 'board';

export function PollPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [stage, setStage] = useState<Stage>('loading');
  const [error, setError] = useState<string | null>(null);
  const [board, setBoard] = useState<PollBoard | null>(null);
  const [nameDraft, setNameDraft] = useState('');

  const reload = useCallback(async () => {
    const b = await fetchPollBoard();
    setBoard(b);
    return b;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const entered = await enterPoll(token);
        const b = await reload();
        if (!alive) return;
        setNameDraft(b.me.display_name ?? entered.suggested_name ?? '');
        setStage(entered.display_name_set ? 'board' : 'intro');
      } catch (e) {
        if (!alive) return;
        setError(
          e instanceof ApiClientError
            ? t(`errors.${e.code}`, { defaultValue: e.message })
            : e instanceof Error
              ? e.message
              : String(e),
        );
        setStage('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, reload, t]);

  const saveName = async () => {
    if (!nameDraft.trim()) return;
    try {
      await setPollName(nameDraft.trim());
      await reload();
      setStage('board');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (stage === 'loading') return <p className="text-sm text-slate-500">…</p>;
  if (stage === 'error')
    return (
      <p role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2">
        {error ?? t('errors.ERR_UNAUTHORIZED')}
      </p>
    );

  if (stage === 'intro' && board)
    return <IntroScreen board={board} onContinue={() => setStage(board.me.display_name_set ? 'board' : 'name')} />;

  if (stage === 'name')
    return (
      <div className="max-w-md mx-auto space-y-4 py-6">
        <h1 className="text-lg font-semibold">{t('poll.name.title')}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t('poll.name.help')}</p>
        <input className={`${INPUT} w-full`} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} />
        <button type="button" className={BTN_PRIMARY} onClick={() => void saveName()}>
          {t('poll.name.continue')}
        </button>
      </div>
    );

  if (!board) return null;
  return (
    <BoardScreen
      board={board}
      onReload={reload}
      onReopenIntro={() => setStage('intro')}
      onEditName={() => setStage('name')}
    />
  );
}

function IntroScreen({ board, onContinue }: { board: PollBoard; onContinue: () => void }) {
  const { t } = useTranslation();
  const steps = t('poll.intro.steps', { returnObjects: true }) as string[];
  return (
    <div className="max-w-lg mx-auto space-y-4 py-6">
      <h1 className="text-xl font-semibold">{t('poll.intro.welcome', { title: board.poll.title })}</h1>
      {board.poll.intro_text && (
        <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap border-l-2 border-slate-300 dark:border-slate-600 pl-3">
          {board.poll.intro_text}
        </p>
      )}
      <div>
        <p className="text-sm font-medium mb-1">{t('poll.intro.howItWorks')}</p>
        <ol className="list-decimal list-inside space-y-1 text-sm text-slate-700 dark:text-slate-300">
          {Array.isArray(steps) && steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>
      {board.poll.deadline && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('poll.intro.deadline', { date: board.poll.deadline })}
        </p>
      )}
      <button type="button" className={BTN_PRIMARY} onClick={onContinue}>
        {t('poll.intro.start')}
      </button>
    </div>
  );
}

function rankLabel(t: ReturnType<typeof useTranslation>['t'], rank: number): string {
  return t('poll.board.choice', { n: rank });
}

/** Colour + short marker for a slot on a calendar day, by its state/claims. */

function slotTone(s: PollBoardSlot): { cls: string; mark: string } {
  const mine = s.my_claim?.status === 'active';
  if (s.state === 'closed') return { cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', mark: '🧹' };
  if (s.state === 'locked') return { cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200', mark: '🔒' };
  if (mine) return { cls: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800/60 dark:text-emerald-100', mark: '✓' };
  if (s.active_claims > 1) return { cls: 'bg-amber-200 text-amber-900 dark:bg-amber-800/60 dark:text-amber-100', mark: '⚠' + s.active_claims };
  if (s.active_claims === 1) return { cls: 'bg-sky-200 text-sky-900 dark:bg-sky-800/60 dark:text-sky-100', mark: '•' };
  return { cls: 'bg-emerald-50 text-emerald-700 border border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300', mark: '+' };
}

function BoardScreen({
  board,
  onReload,
  onReopenIntro,
  onEditName,
}: {
  board: PollBoard;
  onReload: () => Promise<PollBoard>;
  onReopenIntro: () => void;
  onEditName: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [bedsDraft, setBedsDraft] = useState<Record<number, string>>({});
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [openSlotId, setOpenSlotId] = useState<number | null>(null);
  const mine = board.slots
    .filter((s) => s.my_claim?.status === 'active')
    .sort((a, b) => (a.my_claim!.rank - b.my_claim!.rank));

  const doClaim = async (s: PollBoardSlot, beds: number) => {
    try {
      const nextRank = mine.length + 1;
      await claimPollSlot(s.id, s.my_claim?.status === 'active' ? s.my_claim.rank : nextRank, beds);
      await onReload();
    } catch (e) {
      setError(e instanceof ApiClientError ? t(`errors.${e.code}`, { defaultValue: e.message }) : String(e));
    }
  };
  const doUpdateBeds = async (s: PollBoardSlot, beds: number) => {
    try {
      await updatePollClaimBeds(s.id, beds);
      setBedsDraft((d) => {
        const { [s.id]: _, ...rest } = d;
        return rest;
      });
      await onReload();
    } catch (e) {
      setError(e instanceof ApiClientError ? t(`errors.${e.code}`, { defaultValue: e.message }) : String(e));
    }
  };
  const doWithdraw = async (s: PollBoardSlot) => {
    try {
      await withdrawPollClaim(s.id);
      await onReload();
    } catch (e) {
      setError(e instanceof ApiClientError ? t(`errors.${e.code}`, { defaultValue: e.message }) : String(e));
    }
  };

  const reasonMsg: Record<string, string> = {
    draft: t('poll.board.notOpenYet'),
    closed: t('poll.board.closedMsg'),
    finalized: t('poll.board.finalizedMsg'),
    deadline: t('poll.board.deadlinePassed'),
  };

  const badge = (s: PollBoardSlot) => {
    if (s.state === 'closed') return <span className="text-slate-500">🧹 {s.label ?? t('poll.state.closed')}</span>;
    if (s.state === 'locked') return <span className="text-slate-600 dark:text-slate-300">🔒 {s.assignee_name ?? s.claimants[0]?.name ?? t('poll.state.locked')}</span>;
    const names = claimantLabels(
      s.claimants,
      (name, n) => t('poll.board.nameBeds', { name, n }),
      (n) => t('poll.board.andMore', { n }),
    );
    if (s.active_claims > 1) return <span className="text-amber-600" title={t('poll.board.contested', { n: s.active_claims })}>⚠ {names}</span>;
    if (s.active_claims === 1) return <span className="text-emerald-600" title={t('poll.board.claimedBy', { name: s.claimants[0]?.name ?? '' })}>✅ {names}</span>;
    return <span className="text-slate-400">{t('poll.board.open')}</span>;
  };

  // Calendar view: map each covered day → its slot, and page through the months
  // the slots span (reusing the shared CalendarGrid).
  const slotByDate = useMemo(() => {
    const m = new Map<string, PollBoardSlot>();
    for (const s of board.slots) for (const d of dayDatesInclusive(s.check_in, s.check_out)) m.set(d, s);
    return m;
  }, [board.slots]);
  const monthBounds = useMemo(() => {
    const keys = board.slots.flatMap((s) => [s.check_in.slice(0, 7), s.check_out.slice(0, 7)]);
    if (keys.length === 0) { const now = todayInZurich().slice(0, 7); return { min: now, max: now }; }
    return { min: keys.reduce((a, b) => (a < b ? a : b)), max: keys.reduce((a, b) => (a > b ? a : b)) };
  }, [board.slots]);
  const [month, setMonth] = useState<string>(() => board.slots[0]?.check_in.slice(0, 7) ?? todayInZurich().slice(0, 7));
  const monthLabel = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'de', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1)));

  const activeSlot = openSlotId != null ? board.slots.find((s) => s.id === openSlotId) ?? null : null;

  return (
    <div className="max-w-2xl mx-auto space-y-5 py-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="text-xl font-semibold">{board.poll.title}</h1>
        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
          <button type="button" className="underline" onClick={onReopenIntro}>
            ℹ {t('poll.board.howItWorks')}
          </button>
          <button type="button" className="underline" onClick={onEditName}>
            {board.me.display_name ?? t('poll.board.myName')} ✎
          </button>
        </div>
      </div>

      {!board.writable ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-300 dark:text-amber-300 dark:bg-amber-950 dark:border-amber-700 rounded-md px-3 py-2">
          {reasonMsg[board.writable_reason] ?? t('poll.board.deadlinePassed')}
        </p>
      ) : board.poll.deadline ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('poll.board.deadlineOpen', { date: board.poll.deadline })}
        </p>
      ) : null}
      {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}

      {mine.length > 0 && (
        <section>
          <h2 className="font-semibold mb-1">{t('poll.board.myRanking')}</h2>
          <ol className="space-y-1">
            {mine.map((s) => (
              <li key={s.id} className="flex items-center gap-2 text-sm">
                <span className="text-slate-500 tabular-nums w-14">{rankLabel(t, s.my_claim!.rank)}</span>
                <span className="tabular-nums">
                  {s.check_in} → {s.check_out}
                </span>
                <span className="text-slate-500">· {t('poll.board.bedsN', { n: s.my_claim!.beds })}</span>
                <span className="ml-auto">{badge(s)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* view toggle */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">{t('poll.board.allSlots')}</h2>
        <div className="inline-flex rounded-md border border-slate-300 dark:border-slate-600 overflow-hidden text-sm">
          <button
            type="button"
            className={'px-3 py-1 ' + (view === 'list' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200')}
            onClick={() => setView('list')}
          >
            {t('poll.board.viewList')}
          </button>
          <button
            type="button"
            className={'px-3 py-1 ' + (view === 'calendar' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-200')}
            onClick={() => setView('calendar')}
          >
            {t('poll.board.viewCalendar')}
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <section className="space-y-1">
          <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded-md border border-slate-200 dark:border-slate-700">
            {board.slots.map((s) => {
              const claimable = s.state === 'open' && board.writable;
              const isMine = s.my_claim?.status === 'active';
              return (
                <li key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm flex-wrap">
                  <span className="tabular-nums">
                    {s.check_in} → {s.check_out}
                  </span>
                  {s.label && <span className="text-slate-500">· {s.label}</span>}
                  <span className="ml-auto">{badge(s)}</span>
                  {s.state === 'open' && (
                    <button type="button" className={BTN_SECONDARY} onClick={() => setOpenSlotId(s.id)}>
                      💬 {s.comment_count}
                    </button>
                  )}
                  {claimable && !isMine && (
                    <span className="flex items-center gap-1">
                      <input
                        type="number"
                        min={1}
                        aria-label={t('poll.board.beds')}
                        className={`${INPUT} w-16 py-1`}
                        placeholder={String(DEFAULT_CLAIM_BEDS)}
                        value={bedsDraft[s.id] ?? ''}
                        onChange={(e) => setBedsDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                      />
                      <span className="text-xs text-slate-500">{t('poll.board.myBeds')}</span>
                      <button
                        type="button"
                        className={BTN_PRIMARY}
                        onClick={() => void doClaim(s, parseBedsInput(bedsDraft[s.id]))}
                      >
                        {t('poll.board.claim')}
                      </button>
                    </span>
                  )}
                  {claimable && isMine && (() => {
                    const draft = bedsDraft[s.id] ?? String(s.my_claim!.beds);
                    const next = editedBeds(draft, s.my_claim!.beds);
                    return (
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          aria-label={t('poll.board.beds')}
                          className={`${INPUT} w-16 py-1`}
                          value={draft}
                          onChange={(e) => setBedsDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                        />
                        <span className="text-xs text-slate-500">{t('poll.board.myBeds')}</span>
                        <button
                          type="button"
                          className={BTN_PRIMARY}
                          disabled={next === null}
                          onClick={() => next !== null && void doUpdateBeds(s, next)}
                        >
                          {t('poll.board.saveBeds')}
                        </button>
                        <button type="button" className={BTN_SECONDARY} onClick={() => void doWithdraw(s)}>
                          {t('poll.board.withdraw')}
                        </button>
                      </span>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        </section>
      ) : (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              type="button"
              className={BTN_SECONDARY}
              disabled={month <= monthBounds.min}
              onClick={() => setMonth((m) => clampMonth(addMonthsToKey(m, -1), monthBounds.min, monthBounds.max))}
            >
              ‹
            </button>
            <span className="text-sm font-medium">{monthLabel}</span>
            <button
              type="button"
              className={BTN_SECONDARY}
              disabled={month >= monthBounds.max}
              onClick={() => setMonth((m) => clampMonth(addMonthsToKey(m, 1), monthBounds.min, monthBounds.max))}
            >
              ›
            </button>
          </div>
          <CalendarGrid
            month={month}
            onDateClick={(date) => {
              const s = slotByDate.get(date);
              if (s) setOpenSlotId(s.id);
            }}
            cellRenderer={({ date }) => {
              const s = slotByDate.get(date);
              if (!s) return null;
              const { cls, mark } = slotTone(s);
              return (
                <span className={`block w-full text-center rounded px-0.5 text-[10px] sm:text-xs font-medium ${cls}`}>
                  {mark}
                </span>
              );
            }}
          />
          <p className="text-xs text-slate-500">{t('poll.board.calendarHint')}</p>
        </section>
      )}

      {activeSlot && (
        <SlotOverlay
          slot={activeSlot}
          writable={board.writable}
          onClaim={(beds) => doClaim(activeSlot, beds)}
          onUpdateBeds={(beds) => doUpdateBeds(activeSlot, beds)}
          onWithdraw={() => doWithdraw(activeSlot)}
          onClose={() => setOpenSlotId(null)}
          onReload={onReload}
        />
      )}
    </div>
  );
}

function SlotOverlay({
  slot,
  writable,
  onClaim,
  onUpdateBeds,
  onWithdraw,
  onClose,
  onReload,
}: {
  slot: PollBoardSlot;
  writable: boolean;
  onClaim: (beds: number) => Promise<void>;
  onUpdateBeds: (beds: number) => Promise<void>;
  onWithdraw: () => Promise<void>;
  onClose: () => void;
  onReload: () => Promise<PollBoard>;
}) {
  const { t } = useTranslation();
  const [comments, setComments] = useState<PollThreadComment[] | null>(null);
  const [draft, setDraft] = useState('');
  // My own active claim shows its real count (even 0) so it can be edited.
  const [beds, setBeds] = useState(
    slot.my_claim?.status === 'active' || slot.my_claim?.beds ? String(slot.my_claim.beds) : '',
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setComments(await fetchPollThread(slot.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [slot.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async () => {
    if (!draft.trim()) return;
    try {
      await addPollComment(slot.id, draft.trim());
      setDraft('');
      await load();
      await onReload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const isMine = slot.my_claim?.status === 'active';
  const claimable = slot.state === 'open' && writable;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/40 dark:bg-black/60 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-t-lg sm:rounded-lg shadow-xl w-full sm:max-w-lg max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <h3 className="text-sm font-semibold">
            {slot.check_in} → {slot.check_out}
            {slot.label && <span className="text-slate-400"> · {slot.label}</span>}
          </h3>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            ✕
          </button>
        </div>

        <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-sm">
          <p className="font-medium mb-1">{t('poll.thread.whoWants')}</p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1 text-slate-700 dark:text-slate-300">
            {slot.claimants.map((c) => (
              <li key={c.participant_id}>
                {c.name}{' '}
                <span className="text-slate-400">
                  · {t('poll.board.choice', { n: c.rank })} · {t('poll.board.bedsN', { n: c.beds })}
                </span>
                {c.is_me && <span className="text-emerald-600"> ({t('poll.thread.you')})</span>}
              </li>
            ))}
            {slot.claimants.length === 0 && <li className="text-slate-400">—</li>}
          </ul>
        </div>

        {claimable && (
          <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
            {isMine ? (
              <>
                <input
                  type="number"
                  min={0}
                  aria-label={t('poll.board.beds')}
                  className={`${INPUT} w-16 py-1`}
                  value={beds}
                  onChange={(e) => setBeds(e.target.value)}
                />
                <span className="text-xs text-slate-500">{t('poll.board.myBeds')}</span>
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={editedBeds(beds, slot.my_claim!.beds) === null}
                  onClick={() => {
                    const next = editedBeds(beds, slot.my_claim!.beds);
                    if (next !== null) void onUpdateBeds(next);
                  }}
                >
                  {t('poll.board.saveBeds')}
                </button>
                <button type="button" className={BTN_SECONDARY} onClick={() => void onWithdraw()}>
                  {t('poll.board.withdraw')}
                </button>
              </>
            ) : (
              <>
                <input
                  type="number"
                  min={1}
                  aria-label={t('poll.board.beds')}
                  className={`${INPUT} w-16 py-1`}
                  placeholder={String(DEFAULT_CLAIM_BEDS)}
                  value={beds}
                  onChange={(e) => setBeds(e.target.value)}
                />
                <span className="text-xs text-slate-500">{t('poll.board.myBeds')}</span>
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  onClick={() => void onClaim(parseBedsInput(beds))}
                >
                  {t('poll.board.claim')}
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {comments === null && <p className="text-sm text-slate-400">…</p>}
          {comments?.map((co) => (
            <div key={co.id} className="text-sm">
              <span className={'font-medium ' + (co.is_admin ? 'text-sky-700 dark:text-sky-400' : '')}>
                {co.is_admin ? t('poll.thread.admin') : co.author_name ?? t('poll.admin.unnamed')}
              </span>
              <span className="text-slate-400 text-xs"> · {co.created_at}</span>
              <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{co.body}</p>
            </div>
          ))}
          {comments?.length === 0 && <p className="text-sm text-slate-400">{t('poll.thread.empty')}</p>}
        </div>

        {error && <p role="alert" className="px-4 text-sm text-rose-700">{error}</p>}

        {writable && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-3 flex gap-2">
            <input
              className={`${INPUT} flex-1`}
              placeholder={t('poll.thread.placeholder')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void send();
              }}
            />
            <button type="button" className={BTN_PRIMARY} onClick={() => void send()}>
              {t('poll.thread.send')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
